import { useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  useCreate,
  useCustomMutation,
  useDelete,
  useList,
  useUpdate,
} from "@refinedev/core";
import { useTranslation } from "react-i18next";

import { PhoneInput } from "../../../components/PhoneInput";
import { usePageTitle } from "../../../hooks";
import { PageHeader } from "../../../components/common/PageHeader";
import { formatPhone } from "../../../utils/phone";

/**
 * Admin CRUD para user_types no-auditores: 1 Cyber Admin, 2 ESA Admin,
 * 3 Comercial. Los auditores (4 y 5) siguen viviendo en
 * /admin/auditor-assignments porque su flujo necesita audit_company +
 * allowed_portfolios y un patrón visual distinto.
 *
 * Backend: `AdminUserViewSet` en apps/api/v1/admin_users/views.py.
 * El endpoint es `v1/admin/users` — separado del `v1/app-users`
 * legacy (read-only para dropdowns).
 */

const RESOURCE = "v1/admin/users";

// Mismas etiquetas que el backend (`apps/master_data/helpers.py:COUNTRIES`).
const PORTFOLIO_LABELS: Record<number, string> = {
  1: "España",
  2: "Perú",
  3: "Chile",
  4: "Sudáfrica",
  5: "Turquía",
  6: "Egipto",
  7: "Uruguay",
  8: "Portugal",
  9: "Italia",
};

const PORTFOLIO_OPTIONS = Object.entries(PORTFOLIO_LABELS).map(([k, label]) => ({
  value: Number(k),
  label,
}));

// Solo mostramos los 3 roles gestionables desde este endpoint.
const USER_TYPE_OPTIONS = [
  { value: 1, label: "Cyber Admin" },
  { value: 2, label: "ESA Admin" },
  { value: 3, label: "Comercial" },
];

const USER_TYPE_COLORS: Record<number, string> = {
  1: "geekblue",
  2: "orange",
  3: "green",
};

// Popup del Select dentro del modal — evita clipping detrás del overlay.
const popupInsideModal = (node: HTMLElement) =>
  (node?.parentElement as HTMLElement) ?? document.body;

interface AdminUser {
  id: number;
  name: string;
  code: string;
  email: string;
  phone_number: string;
  user_type: 1 | 2 | 3;
  user_type_display: string;
  portfolio: number;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

interface FormValues {
  first_name: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  user_type: 1 | 2 | 3;
  portfolio: number;
  is_active: boolean;
}

export const AdminUsersList = () => {
  const { t } = useTranslation();
  usePageTitle(t("admin_users.title", "Gestión de usuarios"));

  const { message } = AntdApp.useApp();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [form] = Form.useForm<FormValues>();

  const usersQuery = useList<AdminUser>({
    resource: RESOURCE,
    pagination: { pageSize: 200 },
    sorters: [{ field: "name", order: "asc" }],
    filters: search
      ? [{ field: "search", operator: "contains" as any, value: search }]
      : [],
  });

  const rows: AdminUser[] = usersQuery.query.data?.data ?? [];
  const isLoading = usersQuery.query.isLoading;
  const refetch = () => usersQuery.query.refetch();

  const { mutateAsync: createMutate } = useCreate();
  const { mutateAsync: updateMutate } = useUpdate();
  const { mutateAsync: deleteMutate } = useDelete();
  const { mutateAsync: customMutate } = useCustomMutation();
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const removeUser = async (record: AdminUser) => {
    setDeletingId(record.id);
    try {
      await deleteMutate({
        resource: RESOURCE,
        id: record.id,
        successNotification: false,
        errorNotification: false,
      });
      message.success(
        t("admin_users.deleted", "Usuario eliminado"),
      );
      refetch();
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.originalError?.response?.data?.detail ??
        t(
          "admin_users.delete_error",
          "No se pudo eliminar el usuario.",
        );
      message.error(detail);
    } finally {
      setDeletingId(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      user_type: 2,
      portfolio: 1,
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record: AdminUser) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      first_name: record.first_name || "",
      last_name: record.last_name || "",
      email: record.email,
      phone_number: record.phone_number || "",
      user_type: record.user_type,
      portfolio: record.portfolio,
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    try {
      if (editing) {
        await updateMutate({
          resource: RESOURCE,
          id: editing.id,
          values,
        });
        message.success(
          t("admin_users.save_success", "Usuario actualizado"),
        );
      } else {
        await createMutate({
          resource: RESOURCE,
          values,
        });
        message.success(
          t("admin_users.create_success", "Usuario creado"),
        );
      }
      setModalOpen(false);
      refetch();
    } catch (e: any) {
      const data = e?.response?.data ?? e?.originalError?.response?.data;
      const msg =
        typeof data === "string"
          ? data
          : data
            ? Object.entries(data)
                .map(
                  ([k, v]) =>
                    `${k}: ${Array.isArray(v) ? v.join(", ") : v}`,
                )
                .join(" — ")
            : editing
              ? t("admin_users.save_error", "Error al guardar")
              : t("admin_users.create_error", "Error al crear");
      message.error(msg);
    }
  };

  const toggleActive = async (record: AdminUser, next: boolean) => {
    try {
      await updateMutate({
        resource: RESOURCE,
        id: record.id,
        values: { is_active: next },
      });
      message.success(
        next
          ? t("admin_users.activated", "Usuario activado")
          : t("admin_users.deactivated", "Usuario desactivado"),
      );
      refetch();
    } catch {
      message.error(t("admin_users.save_error", "Error al guardar"));
    }
  };

  const sendCredentials = async (record: AdminUser) => {
    setSendingId(record.id);
    try {
      const r = await customMutate({
        url: `/${RESOURCE}/${record.id}/send-credentials/`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      const sent =
        (r?.data as any)?.sent_to ?? record.email ?? record.username;
      message.success(
        t(
          "admin_users.credentials_sent",
          "Credenciales enviadas a {{email}}",
          { email: sent },
        ),
      );
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.originalError?.response?.data?.detail ??
        t(
          "admin_users.credentials_error",
          "No se pudo enviar el email. Comprueba la configuración SMTP y reintenta.",
        );
      message.error(detail);
    } finally {
      setSendingId(null);
    }
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      [r.name, r.email, r.username, r.code]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const columns = [
    {
      title: t("admin_users.col_name", "Nombre"),
      dataIndex: "name",
      key: "name",
      sorter: (a: AdminUser, b: AdminUser) => a.name.localeCompare(b.name),
      render: (v: string, r: AdminUser) => (
        <Space direction="vertical" size={0}>
          <strong>{v || r.username}</strong>
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            @{r.username} · {r.code}
          </span>
        </Space>
      ),
    },
    {
      title: t("admin_users.col_email", "Email"),
      dataIndex: "email",
      key: "email",
      render: (v: string, r: AdminUser) => (
        <Space direction="vertical" size={0}>
          <span>{v || "—"}</span>
          {r.phone_number && (
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>
              {formatPhone(r.phone_number)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: t("admin_users.col_role", "Rol"),
      dataIndex: "user_type",
      key: "user_type",
      filters: USER_TYPE_OPTIONS.map((o) => ({
        text: o.label,
        value: o.value,
      })),
      onFilter: (val: any, r: AdminUser) => r.user_type === val,
      render: (v: number, r: AdminUser) => (
        <Tag color={USER_TYPE_COLORS[v] || "default"}>
          {r.user_type_display}
        </Tag>
      ),
    },
    {
      title: t("admin_users.col_portfolio", "País"),
      dataIndex: "portfolio",
      key: "portfolio",
      render: (v: number) => PORTFOLIO_LABELS[v] || "—",
    },
    {
      title: t("admin_users.col_active", "Activo"),
      dataIndex: "is_active",
      key: "is_active",
      align: "center" as const,
      render: (v: boolean, r: AdminUser) => (
        <Switch
          checked={v}
          onChange={(next) => toggleActive(r, next)}
          checkedChildren={<CheckCircleFilled />}
          unCheckedChildren={<CloseCircleFilled />}
        />
      ),
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 160,
      align: "center" as const,
      render: (_: unknown, r: AdminUser) => (
        <Space size={4}>
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(r)}
            />
          </Tooltip>
          <Tooltip
            title={t(
              "admin_users.send_credentials",
              "Enviar credenciales por email",
            )}
          >
            <Button
              size="small"
              icon={<MailOutlined />}
              loading={sendingId === r.id}
              onClick={() => sendCredentials(r)}
              disabled={!r.email}
            />
          </Tooltip>
          <Popconfirm
            title={t(
              "admin_users.delete_confirm_title",
              "Eliminar usuario",
            )}
            description={
              <div style={{ maxWidth: 320 }}>
                {t(
                  "admin_users.delete_confirm_body",
                  "Se eliminará {{name}} y su cuenta de login asociada. Los registros históricos (auditorías, tickets…) mantienen sus datos pero pierden el enlace al autor. No se puede deshacer.",
                  { name: r.name || r.username },
                )}
              </div>
            }
            okText={t("common.yes", "Sí, eliminar")}
            cancelText={t("common.cancel", "Cancelar")}
            okButtonProps={{ danger: true, loading: deletingId === r.id }}
            onConfirm={() => removeUser(r)}
          >
            <Tooltip title={t("common.delete", "Eliminar")}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deletingId === r.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<UserOutlined />}
        title={t("admin_users.title", "Gestión de usuarios")}
        subtitle={t(
          "admin_users.subtitle",
          "Alta, edición y desactivación de administradores Cyberagropolis, ESA y comerciales. Los auditores viven en su propio panel.",
        )}
        extra={
          <Space>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t(
                "admin_users.search_placeholder",
                "Buscar por nombre, código, email o usuario…",
              )}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 320 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              {t("admin_users.create", "Nuevo usuario")}
            </Button>
          </Space>
        }
      />

      <Table
        rowKey="id"
        dataSource={filteredRows}
        columns={columns}
        loading={isLoading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
      />

      <Modal
        open={modalOpen}
        title={
          editing
            ? t("admin_users.edit_title", "Editar usuario")
            : t("admin_users.create_title", "Nuevo usuario")
        }
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={
          editing
            ? t("common.save", "Guardar")
            : t("common.create", "Crear")
        }
        cancelText={t("common.cancel", "Cancelar")}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("admin_users.first_name", "Nombre")}
                name="first_name"
                rules={[{ required: true }]}
              >
                <Input maxLength={150} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("admin_users.last_name", "Apellidos")}
                name="last_name"
              >
                <Input maxLength={150} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("admin_users.email", "Email")}
                name="email"
                rules={[
                  { required: true },
                  { type: "email" as const },
                ]}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("admin_users.phone_number", "Teléfono")}
                name="phone_number"
              >
                <PhoneInput countryFilter={[
                  "ES", "IT", "PT", "ZA", "PE", "CL", "UY", "EG", "TR",
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("admin_users.user_type", "Rol")}
                name="user_type"
                rules={[{ required: true }]}
                extra={
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {t(
                      "admin_users.role_scope_hint",
                      "Solo Cyber, ESA y Comercial. Auditores en /admin/auditor-assignments.",
                    )}
                  </span>
                }
              >
                <Select
                  options={USER_TYPE_OPTIONS}
                  getPopupContainer={popupInsideModal}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("admin_users.portfolio", "País (portfolio)")}
                name="portfolio"
                rules={[{ required: true }]}
                extra={
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {t(
                      "admin_users.portfolio_hint",
                      "Para ESA scopea todos los datos a ese país.",
                    )}
                  </span>
                }
              >
                <Select
                  options={PORTFOLIO_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                  getPopupContainer={popupInsideModal}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t("admin_users.is_active", "Activo")}
            name="is_active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AdminUsersList;
